import { Model, Attr, BelongsTo } from 'spraypaint'

import ApplicationRecord from '@/models/ApplicationRecord'
import { Caretaker } from '@/models/index'
import request from '@/utils/request'
import moment from 'moment'

@Model()
export default class DotationOperation extends ApplicationRecord {
  static jsonapiType = 'dotation_operations'

  @Attr() id!: string
  @Attr() bid!: string
  @Attr() batchInProcess!: boolean
  @Attr() name!: string
  @Attr() donorName!: string
  @Attr() donorLogo!: string
  @Attr() bannerImage!: string
  @Attr() giftBoxColor!: string
  @Attr() giftRibbonColor!: string
  @Attr() identifier!: string
  @Attr() externalIdentifier!: string
  @Attr() maximumAmount!: number
  @Attr() totalUsedAmount!: number
  @Attr() totalIssuedAmount!: number
  @Attr() dotationDuration!: string
  @Attr() cseKind!: string
  @Attr() preIssue!: boolean
  @Attr() startAt!: string
  @Attr() endAt!: string
  @Attr() createdAt!: string

  @BelongsTo() caretaker!: Caretaker

  get remainingAmount(): number {
    return this.maximumAmount - this.totalIssuedAmount
  }

  async pollBatchStatus(): Promise<any> {
    return await request.get(`/batch_status/${this.bid}.json`)
  }

  public async preIssueCards(multiChoiceCardDistributions: any): Promise<any> {
    const params = {
      data: {
        id: this.id,
        type: 'dotation_operations',
        multi_choice_card_distributions: multiChoiceCardDistributions
      }
    }

    const { data } = await request.put(`/market/v1/dotation_operations/${this.id}/pre_issue`, params)

    return data
  }

  public async give(multiChoiceGiftCardId: string, customerId: string, amount: number, scheduledAt?: string): Promise<any> {
    const today = moment().format('YYYY-MM-DD')
    const effectiveScheduledAt = scheduledAt && moment(scheduledAt).format('YYYY-MM-DD') !== today
      ? scheduledAt
      : undefined

    const params: any = {
      data: {
        id: this.id,
        type: 'dotation_operations',
        multi_choice_gift_card_id: multiChoiceGiftCardId,
        customer_id: customerId,
        amount: amount
      }
    }

    if (effectiveScheduledAt) params.data.scheduled_at = effectiveScheduledAt

    const { data } = await request.put(`/market/v1/dotation_operations/${this.id}/give`, params)

    return data
  }

  public async revoke(multiChoiceCardId: string): Promise<any> {
    const params = {
      data: {
        id: this.id,
        type: 'dotation_operations',
        multi_choice_card_id: multiChoiceCardId
      }
    }

    const { data } = await request.put(`/market/v1/dotation_operations/${this.id}/revoke`, params)

    return data
  }

  public async revokes(multiChoiceCardIds: string[]): Promise<any> {
    const params = {
      data: {
        id: this.id,
        type: 'dotation_operations',
        multi_choice_card_ids: multiChoiceCardIds
      }
    }

    const { data } = await request.put(
      `/market/v1/dotation_operations/revokes?id=${this.id}`,
      params
    )

    return data
  }

  async giveGifts(blob: string): Promise<any> {
    const params = {
      data: {
        id: this.id,
        type: 'dotation_operations',
        blob: blob
      }
    }

    const { data } = await request.put(`/market/v1/dotation_operations/${this.id}/give_gifts`, params, { timeout: 20000 })
    return data
  }

  budgetRemainingPercentage(): number {
    const percentage = Number((100 - (((this.maximumAmount - this.remainingAmount) / this.maximumAmount) * 100)).toFixed(2))

    if (percentage < 0) return 0
    else return percentage
  }

  usagePercentage(): number {
    if (this.totalUsedAmount === 0) return 0
    else return Number(((this.totalUsedAmount / (this.maximumAmount - this.remainingAmount)) * 100).toFixed(2))
  }
}
